import React from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Trash2, Plus, Star } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api, CATEGORIES, CATEGORY_LABEL } from "../lib/api";
import ImageDropzone from "../components/site/ImageDropzone";
import StatsPanel from "../components/site/StatsPanel";

function resolveUrl(u) {
    if (!u) return null;
    if (u.startsWith("http")) return u;
    return `${process.env.REACT_APP_BACKEND_URL}${u}`;
}

export default function AdminDashboard() {
    const { user, loading, logout } = useAuth();
    const [tab, setTab] = React.useState("photos");

    const [photos, setPhotos] = React.useState([]);
    const [quotes, setQuotes] = React.useState([]);
    const [testimonials, setTestimonials] = React.useState([]);

    // photo form
    const [pForm, setPForm] = React.useState({ category: "automobile", url: "", title: "" });
    const [pBusy, setPBusy] = React.useState(false);

    // testimonial form
    const [tForm, setTForm] = React.useState({ name: "", role: "", content: "", rating: 5, photo_url: "" });
    const [tBusy, setTBusy] = React.useState(false);

    const loadPhotos = React.useCallback(() => {
        api.get("/photos").then((r) => setPhotos(r.data));
    }, []);
    const loadQuotes = React.useCallback(() => {
        api.get("/admin/quote-requests").then((r) => setQuotes(r.data)).catch(() => {});
    }, []);
    const loadTestimonials = React.useCallback(() => {
        api.get("/testimonials").then((r) => setTestimonials(r.data));
    }, []);

    React.useEffect(() => {
        if (user && user.role === "admin") {
            loadPhotos();
            loadQuotes();
            loadTestimonials();
        }
    }, [user, loadPhotos, loadQuotes, loadTestimonials]);

    if (loading) return <div className="pt-32 text-center text-zinc-500">Chargement…</div>;
    if (!user || user.role !== "admin") return <Navigate to="/admin/login" replace />;

    const addPhoto = async (e) => {
        e.preventDefault();
        if (!pForm.url) {
            toast.error("Téléversez une image d'abord");
            return;
        }
        setPBusy(true);
        try {
            await api.post("/admin/photos", pForm);
            toast.success("Photo ajoutée");
            setPForm({ category: pForm.category, url: "", title: "" });
            loadPhotos();
        } catch (err) {
            toast.error("Erreur ajout photo");
        } finally {
            setPBusy(false);
        }
    };

    const deletePhoto = async (id) => {
        if (!window.confirm("Supprimer cette photo ?")) return;
        await api.delete(`/admin/photos/${id}`);
        toast.success("Supprimée");
        loadPhotos();
    };

    const deleteQuote = async (id) => {
        if (!window.confirm("Supprimer cette demande ?")) return;
        await api.delete(`/admin/quote-requests/${id}`);
        toast.success("Supprimée");
        loadQuotes();
    };

    const addTestimonial = async (e) => {
        e.preventDefault();
        setTBusy(true);
        try {
            const payload = { ...tForm };
            if (!payload.role) delete payload.role;
            if (!payload.photo_url) delete payload.photo_url;
            await api.post("/admin/testimonials", payload);
            toast.success("Témoignage ajouté");
            setTForm({ name: "", role: "", content: "", rating: 5, photo_url: "" });
            loadTestimonials();
        } catch (err) {
            toast.error("Erreur ajout témoignage");
        } finally {
            setTBusy(false);
        }
    };

    const deleteTestimonial = async (id) => {
        if (!window.confirm("Supprimer ce témoignage ?")) return;
        await api.delete(`/admin/testimonials/${id}`);
        toast.success("Supprimé");
        loadTestimonials();
    };

    return (
        <div data-testid="page-admin" className="pt-28 pb-24 px-6 md:px-12 lg:px-24">
            <div className="flex items-center justify-between border-b border-white/10 pb-6 mb-10 flex-wrap gap-4">
                <div>
                    <p className="text-xs uppercase tracking-[0.3em] text-[#c5a059]">Control Room</p>
                    <h1 className="font-serif-display text-4xl mt-1">Tableau de bord</h1>
                    <p className="text-zinc-500 text-sm mt-2">Connecté en tant que {user.email}</p>
                </div>
                <button
                    data-testid="admin-logout"
                    onClick={logout}
                    className="flex items-center gap-2 border border-white/15 px-4 py-2 text-xs uppercase tracking-[0.2em] hover:bg-white hover:text-black transition-colors"
                >
                    <LogOut size={14} strokeWidth={1.5} /> Déconnexion
                </button>
            </div>

            <div className="flex flex-wrap gap-2 mb-10">
                <TabBtn active={tab === "photos"} onClick={() => setTab("photos")} testId="tab-photos">
                    Photos ({photos.length})
                </TabBtn>
                <TabBtn active={tab === "quotes"} onClick={() => setTab("quotes")} testId="tab-quotes">
                    Demandes ({quotes.length})
                </TabBtn>
                <TabBtn active={tab === "testimonials"} onClick={() => setTab("testimonials")} testId="tab-testimonials">
                    Témoignages ({testimonials.length})
                </TabBtn>
                <TabBtn active={tab === "stats"} onClick={() => setTab("stats")} testId="tab-stats">
                    Statistiques
                </TabBtn>
            </div>

            {tab === "stats" && <StatsPanel />}

            {tab === "photos" && (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
                    <form onSubmit={addPhoto} data-testid="admin-add-photo-form" className="lg:col-span-1 bg-[#0f0f0f] border border-white/10 p-6 rounded-md h-fit space-y-5">
                        <h2 className="font-serif-display text-2xl">Ajouter une photo</h2>

                        <label className="block">
                            <span className="text-xs uppercase tracking-[0.2em] text-zinc-400">Catégorie</span>
                            <select
                                data-testid="admin-photo-category"
                                value={pForm.category}
                                onChange={(e) => setPForm({ ...pForm, category: e.target.value })}
                                className="mt-2 w-full bg-transparent border border-white/15 px-3 py-2 text-white focus:border-[#c5a059] outline-none"
                            >
                                {CATEGORIES.map((c) => (
                                    <option key={c.key} value={c.key}>{c.label}</option>
                                ))}
                            </select>
                        </label>

                        <div>
                            <span className="text-xs uppercase tracking-[0.2em] text-zinc-400">Image</span>
                            <div className="mt-2">
                                <ImageDropzone
                                    testId="admin-photo-dropzone"
                                    onUploaded={(url) => setPForm((f) => ({ ...f, url: url || "" }))}
                                />
                            </div>
                        </div>

                        <label className="block">
                            <span className="text-xs uppercase tracking-[0.2em] text-zinc-400">Titre (optionnel)</span>
                            <input
                                data-testid="admin-photo-title"
                                value={pForm.title}
                                onChange={(e) => setPForm({ ...pForm, title: e.target.value })}
                                className="mt-2 w-full bg-transparent border border-white/15 px-3 py-2 text-white focus:border-[#c5a059] outline-none"
                            />
                        </label>

                        <button
                            data-testid="admin-photo-submit"
                            type="submit"
                            disabled={pBusy || !pForm.url}
                            className="w-full bg-[#c5a059] text-black font-medium py-3 uppercase tracking-[0.2em] text-xs hover:bg-[#dcc082] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                        >
                            <Plus size={14} strokeWidth={1.5} /> {pBusy ? "Ajout…" : "Ajouter"}
                        </button>
                    </form>

                    <div className="lg:col-span-2">
                        <h2 className="font-serif-display text-2xl mb-6">Galerie ({photos.length})</h2>
                        {photos.length === 0 ? (
                            <p className="text-zinc-500">Aucune photo.</p>
                        ) : (
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                {photos.map((p) => (
                                    <div key={p.id} data-testid={`admin-photo-${p.id}`} className="relative group border border-white/10">
                                        <img src={resolveUrl(p.url)} alt={p.title || ""} className="w-full aspect-square object-cover" loading="lazy" />
                                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/60 transition-colors flex items-center justify-center">
                                            <button
                                                data-testid={`admin-photo-delete-${p.id}`}
                                                onClick={() => deletePhoto(p.id)}
                                                className="opacity-0 group-hover:opacity-100 bg-red-500/90 text-white px-3 py-2 text-xs uppercase tracking-[0.2em] flex items-center gap-2"
                                            >
                                                <Trash2 size={14} strokeWidth={1.5} /> Supprimer
                                            </button>
                                        </div>
                                        <div className="p-2 text-xs">
                                            <span className="text-[#c5a059] uppercase tracking-[0.15em]">{CATEGORY_LABEL[p.category]}</span>
                                            {p.title && <p className="text-zinc-300 truncate">{p.title}</p>}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {tab === "quotes" && (
                <div data-testid="admin-quotes">
                    <h2 className="font-serif-display text-2xl mb-6">Demandes de devis</h2>
                    {quotes.length === 0 ? (
                        <p className="text-zinc-500">Aucune demande pour l'instant.</p>
                    ) : (
                        <div className="space-y-4">
                            {quotes.map((q) => (
                                <div key={q.id} data-testid={`quote-${q.id}`} className="bg-[#0f0f0f] border border-white/10 p-6 rounded-md">
                                    <div className="flex items-start justify-between gap-4 flex-wrap">
                                        <div>
                                            <p className="text-xs uppercase tracking-[0.2em] text-[#c5a059]">{CATEGORY_LABEL[q.prestation_type]}</p>
                                            <h3 className="font-serif-display text-2xl mt-1">{q.name}</h3>
                                            <p className="text-zinc-400 text-sm mt-1">
                                                {q.email} {q.phone && `• ${q.phone}`}
                                            </p>
                                            {q.event_date && (
                                                <p className="text-zinc-500 text-xs mt-1">Date prévue : {q.event_date}</p>
                                            )}
                                        </div>
                                        <button
                                            data-testid={`quote-delete-${q.id}`}
                                            onClick={() => deleteQuote(q.id)}
                                            className="text-red-400 hover:text-red-300 text-xs uppercase tracking-[0.2em] flex items-center gap-2"
                                        >
                                            <Trash2 size={14} strokeWidth={1.5} /> Supprimer
                                        </button>
                                    </div>
                                    <p className="text-zinc-300 mt-4 leading-relaxed whitespace-pre-wrap">{q.message}</p>
                                    <p className="text-zinc-600 text-xs mt-3">Reçu le {new Date(q.created_at).toLocaleString("fr-FR")}</p>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {tab === "testimonials" && (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
                    <form onSubmit={addTestimonial} data-testid="admin-add-testimonial-form" className="lg:col-span-1 bg-[#0f0f0f] border border-white/10 p-6 rounded-md h-fit space-y-5">
                        <h2 className="font-serif-display text-2xl">Ajouter un témoignage</h2>

                        <label className="block">
                            <span className="text-xs uppercase tracking-[0.2em] text-zinc-400">Nom *</span>
                            <input
                                data-testid="admin-testimonial-name"
                                required
                                value={tForm.name}
                                onChange={(e) => setTForm({ ...tForm, name: e.target.value })}
                                className="mt-2 w-full bg-transparent border border-white/15 px-3 py-2 text-white focus:border-[#c5a059] outline-none"
                            />
                        </label>

                        <label className="block">
                            <span className="text-xs uppercase tracking-[0.2em] text-zinc-400">Contexte (optionnel)</span>
                            <input
                                data-testid="admin-testimonial-role"
                                placeholder="ex: Mariage — juin 2024"
                                value={tForm.role}
                                onChange={(e) => setTForm({ ...tForm, role: e.target.value })}
                                className="mt-2 w-full bg-transparent border border-white/15 px-3 py-2 text-white focus:border-[#c5a059] outline-none"
                            />
                        </label>

                        <label className="block">
                            <span className="text-xs uppercase tracking-[0.2em] text-zinc-400">Témoignage *</span>
                            <textarea
                                data-testid="admin-testimonial-content"
                                required
                                rows={5}
                                value={tForm.content}
                                onChange={(e) => setTForm({ ...tForm, content: e.target.value })}
                                className="mt-2 w-full bg-transparent border border-white/15 px-3 py-2 text-white focus:border-[#c5a059] outline-none resize-none"
                            />
                        </label>

                        <label className="block">
                            <span className="text-xs uppercase tracking-[0.2em] text-zinc-400">Note</span>
                            <select
                                data-testid="admin-testimonial-rating"
                                value={tForm.rating}
                                onChange={(e) => setTForm({ ...tForm, rating: parseInt(e.target.value) })}
                                className="mt-2 w-full bg-transparent border border-white/15 px-3 py-2 text-white focus:border-[#c5a059] outline-none"
                            >
                                {[5, 4, 3, 2, 1].map((n) => (
                                    <option key={n} value={n}>{n} étoile{n > 1 ? "s" : ""}</option>
                                ))}
                            </select>
                        </label>

                        <div>
                            <span className="text-xs uppercase tracking-[0.2em] text-zinc-400">Photo (optionnel)</span>
                            <div className="mt-2">
                                <ImageDropzone
                                    testId="admin-testimonial-photo"
                                    label="Glissez une photo de profil"
                                    onUploaded={(url) => setTForm((f) => ({ ...f, photo_url: url || "" }))}
                                />
                            </div>
                        </div>

                        <button
                            data-testid="admin-testimonial-submit"
                            type="submit"
                            disabled={tBusy}
                            className="w-full bg-[#c5a059] text-black font-medium py-3 uppercase tracking-[0.2em] text-xs hover:bg-[#dcc082] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                        >
                            <Plus size={14} strokeWidth={1.5} /> {tBusy ? "Ajout…" : "Ajouter"}
                        </button>
                    </form>

                    <div className="lg:col-span-2 space-y-4">
                        <h2 className="font-serif-display text-2xl mb-2">Témoignages ({testimonials.length})</h2>
                        {testimonials.length === 0 ? (
                            <p className="text-zinc-500">Aucun témoignage.</p>
                        ) : (
                            testimonials.map((t) => (
                                <div key={t.id} data-testid={`admin-testimonial-${t.id}`} className="bg-[#0f0f0f] border border-white/10 p-6 rounded-md">
                                    <div className="flex items-start justify-between gap-4 flex-wrap">
                                        <div className="flex items-center gap-4">
                                            {t.photo_url && (
                                                <img src={resolveUrl(t.photo_url)} alt={t.name} className="w-12 h-12 object-cover rounded-full border border-white/10" />
                                            )}
                                            <div>
                                                <h3 className="font-serif-display text-xl">{t.name}</h3>
                                                {t.role && <p className="text-xs uppercase tracking-[0.2em] text-zinc-500">{t.role}</p>}
                                                <div className="flex gap-1 mt-1">
                                                    {Array.from({ length: t.rating }).map((_, i) => (
                                                        <Star key={i} size={12} strokeWidth={1.5} className="fill-[#c5a059] text-[#c5a059]" />
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                        <button
                                            data-testid={`admin-testimonial-delete-${t.id}`}
                                            onClick={() => deleteTestimonial(t.id)}
                                            className="text-red-400 hover:text-red-300 text-xs uppercase tracking-[0.2em] flex items-center gap-2"
                                        >
                                            <Trash2 size={14} strokeWidth={1.5} /> Supprimer
                                        </button>
                                    </div>
                                    <p className="text-zinc-300 mt-4 leading-relaxed">{t.content}</p>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

function TabBtn({ active, onClick, testId, children }) {
    return (
        <button
            data-testid={testId}
            onClick={onClick}
            className={`px-5 py-2 text-xs uppercase tracking-[0.2em] border transition-colors ${
                active ? "bg-[#c5a059] text-black border-[#c5a059]" : "border-white/15 text-zinc-300 hover:text-white hover:border-white/40"
            }`}
        >
            {children}
        </button>
    );
}
